<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.branding.hero.title')"
      :paragraph="t('jc.view.branding.hero.text')"
      :image="{
        src: 'images/view/branding/hero_banner.png',
        alt: t('jc.view.branding.hero.banner.alt'),
      }"
      :float="{
        src: 'images/skin_renders/compositions/reading_news.png',
        alt: t('jc.view.branding.hero.float.alt'),
      }"
      :label="t('jc.view.branding.hero.button.label')"
      :icon="'download'"
      :to="config.public.brandingKitUrl"
      :external="true"
    />
  </Container>

  <Container
    id="logo"
  >
    <ContentCard
      :heading="t('jc.view.branding.logo.title')"
      :paragraph="t('jc.view.branding.logo.text')"
    >
      <div class="grid grid-cols-1 laptop:grid-cols-3 gap-4 mt-4">
        <ImageAsset
          path="/images/view/branding/logo_transparent"
          png
          svg
        />

        <ImageAsset
          path="/images/view/branding/logo_dungeon_explorer_transparent"
          png
        />
      </div>
    </ContentCard>
  </Container>

  <Container
    id="icon"
  >
    <ContentCard
      :heading="t('jc.view.branding.icon.title')"
      :paragraph="t('jc.view.branding.icon.text')"
    >
      <div class="grid grid-cols-1 laptop:grid-cols-3 gap-4 mt-4">
        <ImageAsset
          path="/images/view/branding/icon_transparent"
          png
          svg
        />
      </div>
    </ContentCard>
  </Container>

  <Container
    id="colors"
  >
    <ContentCard
      :heading="t('jc.view.branding.colors.title')"
      :paragraph="t('jc.view.branding.colors.text')"
    >
      <div class="grid grid-cols-1 laptop:grid-cols-3 gap-4 mt-4">
        <div class="flex flex-col gap-1 bg-primary text-secondary-highlighted border border-gray-200 p-4 rounded">
          <span class="font-semibold mb-6 text-lg">{{ t('jc.view.branding.colors.green') }}</span>
          <span class="opacity-90">#24BE62</span>
          <span class="opacity-90">RGB 36, 190, 98</span>
          <span class="opacity-90">CMYK 0.81, 0.00, 0.48, 0.25</span>
        </div>
        <div class="flex flex-col gap-1 bg-secondary text-white border border-gray-200 p-4 rounded">
          <span class="font-semibold mb-6 text-lg">{{ t('jc.view.branding.colors.gray') }}</span>
          <span class="opacity-90">#161616</span>
          <span class="opacity-90">RGB 22, 22, 22</span>
          <span class="opacity-90">CMYK 0.00, 0.00, 0.00, 0.91</span>
        </div>
        <div class="flex flex-col gap-1 bg-white text-secondary-highlighted border border-gray-200 p-4 rounded">
          <span class="font-semibold mb-6 text-lg">{{ t('jc.view.branding.colors.white') }}</span>
          <span class="opacity-90">#FFFFFF</span>
          <span class="opacity-90">RGB 255, 255, 255</span>
          <span class="opacity-90">CMYK 0.00, 0.00, 0.00, 0.00</span>
        </div>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="fonts"
  >
    <ContentCard
      :heading="t('jc.view.branding.fonts.title')"
      :paragraph="t('jc.view.branding.fonts.text')"
    >
      <div class="grid grid-cols-1 laptop:grid-cols-3 gap-4 mt-4">
        <div
          class="flex flex-col justify-between gap-1 text-gray-900 bg-checkered-white bg-repeat bg-[length:30px] border border-gray-200 p-4 rounded font-poppins"
        >
          <span class="font-semibold mb-6 text-xl">Poppins</span>
          <span class="opacity-90 font-regular">Regular <span class="text-sm opacity-60 ml-1">400</span></span>
          <span class="opacity-90 font-semibold">Semibold <span class="text-sm opacity-60 ml-1">600</span></span>
          <span class="opacity-90 font-bold">Bold <span class="text-sm opacity-60 ml-1">700</span></span>
        </div>
        <div
          class="flex flex-col justify-between gap-1 text-gray-900 bg-checkered-white bg-repeat bg-[length:30px] border border-gray-200 p-4 rounded font-bebas"
        >
          <span class="font-semibold mb-6 text-xl">Bebas Neue</span>
          <span class="opacity-90 font-regular text-lg">Regular <span class="text-md opacity-60 ml-1">400</span></span>
        </div>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="call-to-action"
  >
    <ImageCard
      :heading="t('jc.view.branding.callToAction.title')"
      :paragraph="t('jc.view.branding.callToAction.text')"
      :image="{
        src: 'images/view/branding/call_to_action_background.png',
        alt: t('jc.view.branding.callToAction.background.alt'),
      }"
    >
      <NuxtLink
        :target="'_blank'"
        :to="config.public.brandingKitUrl"
        :external="true"
        :title="t('jc.view.branding.callToAction.button.title')"
        class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white
          text-center rounded py-2 px-4 mt-10 mx-auto font-semibold"
      >
        <font-awesome-icon
          icon="download"
          class="mr-2"
        />
        <span>{{ t('jc.view.branding.callToAction.button.label') }}</span>
      </NuxtLink>
    </ImageCard>
  </Container>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ImageAsset from '~/components/branding/ImageAsset.vue'

const { t } = useI18n()
const config = useRuntimeConfig()

definePageMeta({
  nameKey: 'branding',
  head: {
    imageUrl: '/images/view/branding/meta_preview.png',
  },
})
</script>

<style scoped lang="postcss">
:deep(.preview-img img) {
  @apply w-full h-full object-center object-scale-down max-h-32
}
</style>
